import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useLocation, useNavigate } from 'react-router-dom';
import { agreeTerms, getTermsStatus } from '../api/terms';
import styles from './TermsReagreementPage.module.css';

const TermsReagreementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [missingDocs, setMissingDocs] = useState([]);
  const [checked, setChecked] = useState({});
  const [viewedDocIds, setViewedDocIds] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('next');
    try {
      if (raw) {
        const url = new URL(raw, window.location.origin);
        if (url.origin === window.location.origin) return raw;
      }
    } catch {
      // 無効なURLはデフォルトにフォールバック
    }
    return '/profile/my';
  }, [location.search]);

  useEffect(() => {
    const load = async () => {
      try {
        const status = await getTermsStatus();
        const missing = Array.isArray(status?.missing) ? status.missing : [];
        setMissingDocs(missing);

        if (!status?.requires_reagreement || missing.length === 0) {
          navigate(nextPath, { replace: true });
        }
      } catch (e) {
        setError('再同意情報の取得に失敗しました。時間をおいて再度お試しください。');
      }
    };

    load();
  }, [navigate, nextPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const unchecked = missingDocs.find((doc) => !checked[doc.terms_version_id]);
    if (unchecked) {
      setError('未同意の文書があります。すべて確認の上チェックしてください。');
      return;
    }

    setSubmitting(true);
    try {
      await agreeTerms(
        missingDocs.map((doc) => ({ terms_version_id: doc.terms_version_id }))
      );
      setSuccess('同意を保存しました。');
      navigate(nextPath, { replace: true });
    } catch (e) {
      const message = e?.response?.data?.message || '同意の保存に失敗しました。';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>規約の再同意が必要です</h2>
      <p className={styles.description}>
        規約が更新されたため、サービス継続利用には最新文書への同意が必要です。
      </p>

      {error && <p className={styles.error}>{error}</p>}
      {success && <p className={styles.success}>{success}</p>}

      <form onSubmit={handleSubmit}>
        <ul className={styles.list}>
          {missingDocs.map((doc) => (
            <li key={doc.terms_version_id} className={styles.item}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={!!checked[doc.terms_version_id]}
                  disabled={!viewedDocIds[doc.terms_version_id]}
                  onChange={(e) => {
                    setChecked((prev) => ({
                      ...prev,
                      [doc.terms_version_id]: e.target.checked,
                    }));
                  }}
                />
                <span>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => {
                      setViewedDocIds((prev) => ({ ...prev, [doc.terms_version_id]: true }));
                      setPreviewDoc(doc);
                    }}
                    disabled={submitting}
                  >
                    {doc.title}
                  </button>
                  {' '}に同意します。
                </span>
              </label>
            </li>
          ))}
        </ul>
        {missingDocs.length > 0 && (
          <p className={styles.hint}>※各文書を開くとチェックできるようになります。</p>
        )}

        <button type="submit" className={styles.button} disabled={submitting || missingDocs.length === 0}>
          {submitting ? '保存中...' : '同意して続行'}
        </button>
      </form>

      {previewDoc && (
        <div
          role="dialog"
          aria-modal="true"
          className={styles.modalOverlay}
          onClick={() => setPreviewDoc(null)}
        >
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{previewDoc.title}</h3>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewDoc.content || '') }} />
            <div className={styles.modalFooter}>
              <button type="button" className={styles.closeButton} onClick={() => setPreviewDoc(null)}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsReagreementPage;
